import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Stack, TextField, Chip, IconButton, Tooltip, Typography, InputAdornment, Button, FormControlLabel } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Description, Search, Refresh, Add, CheckCircle, Cancel, Delete, Edit, RestoreFromTrash } from '@mui/icons-material';
import MainText from '../../components/MainText';
import CustomSwitch from '../../components/CustomSwitch';
import CleanningButton from '../../components/CleanningButton';
import CustomModal from '../../components/CustomModal';
import CustomSnackBar from '../../components/CustomSnackBar';
import { RBAC } from '../../services/api';
import { useDispatch } from 'react-redux';
import { addTab } from '../../tabs/tabsSlice';

const getErrorMessage = (err, fallback) => {
  if (!err?.response) {
    const msg = err?.message || '';
    if (typeof msg === 'string' && msg.toLowerCase().includes('network')) {
      return 'Error de red. Verifica tu conexion.';
    }
    if (err?.code === 'ERR_NETWORK') {
      return 'Error de red. Verifica tu conexion.';
    }
    return fallback;
  }

  const data = err?.response?.data;
  if (typeof data === 'string') {
    const isHtml = /<\s*!doctype|<\s*html|<\s*body|<\s*table|<\s*pre/i.test(data);
    if (isHtml) {
      if (err?.response?.status === 413) {
        return 'El archivo es demasiado grande (413)';
      }
      return 'Error interno del servidor. Revisa los logs.';
    }
    return data;
  }
  if (!data) return fallback;
  if (data.detail) return data.detail;
  if (data.message) {
    if (typeof data.message === 'string') return data.message;
    if (typeof data.message === 'object') {
      const key = Object.keys(data.message)[0];
      const val = data.message?.[key];
      if (Array.isArray(val)) return val.join(' ');
      if (typeof val === 'string') return val;
    }
  }
  if (data.name) {
    if (Array.isArray(data.name)) return data.name.join(' ');
    if (typeof data.name === 'string') return data.name;
  }
  const key = Object.keys(data)[0];
  if (key) {
    const val = data[key];
    if (Array.isArray(val)) return val.join(' ');
    if (typeof val === 'string') return val;
  }
  return fallback;
};


function InvoiceTypeForm({ initial, onSubmit }) {
  const [form, setForm] = useState({ name: initial?.name || '', is_active: initial?.is_active ?? true });
  const handleSave = () => onSubmit(form);
  return (
    <Stack spacing={2} sx={{ p: 1 }}>
      <TextField size="small" label="Nombre del tipo de factura" value={form.name} onChange={e => setForm(v => ({ ...v, name: e.target.value }))} />
      <FormControlLabel control={<CustomSwitch name="is_active" label={form.is_active ? 'Activo' : 'Inactivo'} initialValue={form.is_active} onChange={(_, val) => setForm(v => ({ ...v, is_active: val }))} />} label="" />
      <Box sx={{ textAlign: 'right' }}>
        <Button variant="contained" onClick={handleSave}>Guardar</Button>
      </Box>
    </Stack>
  );
}

export default function InvoiceTypeList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: '', active: true });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSev, setSnackSev] = useState('info');
  const dispatch = useDispatch();

  useEffect(() => { dispatch(addTab({ label: 'Tipos de factura', path: '/catalogs/invoice-types' })); }, [dispatch]);

  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(filters.search || ''), 450); return () => clearTimeout(t); }, [filters.search]);
  const handleFilter = (name, value) => setFilters(v => ({ ...v, [name]: value }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await RBAC.invoiceTypes.list({ active: filters.active ? 'true' : 'false' });
      const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      let out = data.map(x => ({ id: x.id, ...x }));
      out = out.filter(r => (filters.active ? r.is_active : !r.is_active));
      const s = (debouncedSearch || '').toLowerCase().trim();
      if (s) out = out.filter(r => String(r.name || '').toLowerCase().includes(s));
      setRows(out);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [debouncedSearch, filters.active]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditRow(null); setDialogOpen(true); };
  const openEdit = (row) => { setEditRow(row); setDialogOpen(true); };
  const handleSubmit = async (payload) => {
    try {
      if (editRow) await RBAC.invoiceTypes.update(editRow.id, payload);
      else await RBAC.invoiceTypes.create(payload);
      setDialogOpen(false); setEditRow(null); load();
      setSnackMsg(editRow ? 'Tipo de factura actualizado' : 'Tipo de factura creado'); setSnackSev('success'); setSnackOpen(true);
    } catch (e) {
      setSnackMsg(getErrorMessage(e, 'No se pudo guardar el tipo de factura'));
      setSnackSev('error');
      setSnackOpen(true);
    }
  };
  const toggleActive = async (row) => {
    try {
      await RBAC.invoiceTypes.update(row.id, { is_active: !row.is_active });
      load();
      setSnackMsg(row.is_active ? 'Tipo desactivado' : 'Tipo activado');
      setSnackSev('success');
      setSnackOpen(true);
    } catch (e) {
      setSnackMsg(getErrorMessage(e, 'No se pudo actualizar el tipo de factura'));
      setSnackSev('error');
      setSnackOpen(true);
    }
  };
  const removeRow = async (row) => {
    if (!window.confirm('¿Eliminar permanentemente?')) return;
    try {
      await RBAC.invoiceTypes.remove(row.id);
      load();
      setSnackMsg('Tipo eliminado');
      setSnackSev('success');
      setSnackOpen(true);
    } catch (e) {
      setSnackMsg(getErrorMessage(e, 'No se puede eliminar porque tiene datos vinculados'));
      setSnackSev('error');
      setSnackOpen(true);
    }
  };

  const columns = useMemo(() => ([
    { field: 'id', headerName: 'ID', width: 80, renderCell: (p) => <Typography variant="caption" color="text.secondary">{p.value}</Typography> },
    { field: 'name', headerName: 'Tipo de factura', flex: 1, minWidth: 260, renderCell: ({ value }) => <Typography sx={{ fontWeight: 700 }}>{value || '—'}</Typography> },
    { field: 'is_active', headerName: 'Estado', width: 120, renderCell: ({ value }) => <Chip size="small" label={value ? 'Activo' : 'Inactivo'} color={value ? 'success' : 'default'} variant="outlined" /> },
    { field: 'actions', headerName: 'Acciones', width: 180, sortable: false, renderCell: ({ row }) => (
      <Stack direction="row" spacing={0.5}>
        <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(row)}><Edit fontSize="small" /></IconButton></Tooltip>
        {filters.active ? (
          <Tooltip title={row.is_active ? 'Desactivar' : 'Activar'}>
            <IconButton size="small" onClick={() => toggleActive(row)}>
              {row.is_active ? <Cancel fontSize="small" color="warning" /> : <CheckCircle fontSize="small" color="success" />}
            </IconButton>
          </Tooltip>
        ) : (
          <>
            <Tooltip title="Restaurar">
              <IconButton size="small" color="success" onClick={() => toggleActive(row)}>
                <RestoreFromTrash fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar permanentemente">
              <IconButton size="small" color="error" onClick={() => removeRow(row)}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>
    ) },
  ]), [filters.active]);

  const clearAll = () => setFilters({ search: '', active: true });

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" sx={{ mb: 2, gap: 2 }}>
        <MainText title="Tipos de factura" subtitle="Catálogo de tipos de factura" icon={Description} width="auto" />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {filters.search && (<Chip label={`Búsqueda: "${filters.search}"`} onDelete={() => handleFilter('search', '')} />)}
            {!filters.active && (<Chip label="Inactivos" color="error" onDelete={() => handleFilter('active', true)} />)}
            {!filters.search && filters.active && (<Chip label="Mostrando activos" color="info" />)}
          </Box>
          <CleanningButton onClick={clearAll} />
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" gap={1.5} sx={{ mb: 1 }}>
        <TextField size="small" placeholder="Buscar por nombre..." value={filters.search} onChange={(e) => handleFilter('search', e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" /></InputAdornment>) }} sx={{ minWidth: { xs: '100%', md: 320 } }} />
        <CustomSwitch name="active" label={filters.active ? 'Activos' : 'Inactivos'} initialValue={filters.active} onChange={(_, val) => handleFilter('active', val)} />
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Refrescar"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
          <Button onClick={openCreate} startIcon={<Add />} size="small" variant="contained" sx={{ borderRadius: 2, px: 1.5, textTransform: 'none', fontWeight: 700, boxShadow: 'none', '&:hover': { boxShadow: 'none', transform: 'translateY(-1px)' } }}>Crear</Button>
        </Box>
      </Stack>

      <Box sx={{ height: 460, width: '100%' }}>
        <DataGrid rows={rows} columns={columns} loading={loading} disableColumnFilter disableColumnMenu pageSizeOptions={[10, 20, 50]} initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }} rowHeight={52} columnHeaderHeight={50} sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#10173c', color: '#fff' }, '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover': { backgroundColor: '#10173c !important' }, '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator': { color: '#ffffff !important' } }} />
      </Box>

      <CustomModal open={dialogOpen} setOpen={(v) => { setDialogOpen(v); if (!v) setEditRow(null); }} title={editRow ? 'Editar tipo de factura' : 'Crear tipo de factura'} component={InvoiceTypeForm} componentProps={{ initial: editRow, onSubmit: handleSubmit }} width="520px" />
      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}
